import * as React from "react";
import { useTranslation } from "react-i18next";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { AutoAdvanceCollapse, AutoAdvanceTimer, useAutoAdvance } from "~/components/ui/auto-advance-accordion";
import { imageUrl } from "~/lib/image-url";
import { cn } from "~/lib/utils";

interface Persona {
  key: "service" | "delivery" | "rental" | "turo" | "charitable" | "public_sector";
  image: string;
  /** Destination pages don't exist yet. */
  href: string;
}

const PERSONA_HREF_PLACEHOLDER = "#";

// Learn More links stay hidden until the persona landing pages exist.
const SHOW_LEARN_MORE = false;

const PERSONAS: Persona[] = [
  { key: "service", image: "service", href: PERSONA_HREF_PLACEHOLDER },
  { key: "delivery", image: "delivery", href: PERSONA_HREF_PLACEHOLDER },
  { key: "rental", image: "rental", href: PERSONA_HREF_PLACEHOLDER },
  { key: "turo", image: "turo", href: PERSONA_HREF_PLACEHOLDER },
  { key: "charitable", image: "charitable", href: PERSONA_HREF_PLACEHOLDER },
  { key: "public_sector", image: "public-sector", href: PERSONA_HREF_PLACEHOLDER },
];

// The list's tallest open state per breakpoint, measured at each range's narrowest
// width (360/640/768/1024/1280). English mobile keeps Figma's 520px; Spanish and
// French wrap identically. Without the Learn More link, each is 36px shorter.
const LIST_MIN_HEIGHT = SHOW_LEARN_MORE
  ? {
      en: "min-h-[520px] sm:min-h-[420px] lg:min-h-[444px] xl:min-h-[420px]",
      other: "min-h-[536px] sm:min-h-[444px] md:min-h-[420px] lg:min-h-[468px] xl:min-h-[444px]",
    }
  : {
      en: "min-h-[484px] sm:min-h-[384px] lg:min-h-[408px] xl:min-h-[384px]",
      other: "min-h-[500px] sm:min-h-[408px] md:min-h-[384px] lg:min-h-[432px] xl:min-h-[408px]",
    };

// Industry accordion beside a crossfading photo. The list reserves its tallest
// open state per breakpoint so switching items never shifts the page.
// Must render inside the .fleet token scope.
export function FleetPersonaRouter() {
  const { t, i18n } = useTranslation("fleet");
  const sectionRef = React.useRef<HTMLElement>(null);
  const { displayIndex, userHasInteracted, select } = useAutoAdvance(PERSONAS.length, sectionRef);
  const imageIndex = displayIndex ?? 0;

  return (
    <section ref={sectionRef} aria-labelledby="fleet-persona-title" className="py-8 lg:py-[60px]">
      <div className="wrap flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
        <div className="flex w-full flex-col gap-6 lg:max-w-[580px] lg:flex-1 lg:gap-8">
          <h2
            id="fleet-persona-title"
            className="font-display text-text-heading max-w-[327px] text-xl leading-7 font-extrabold lg:max-w-[567px] lg:text-4xl lg:leading-[1.1]"
          >
            {t("persona.title")}
          </h2>
          {/* overflow-anchor:none stops scroll anchoring from chasing the list's height changes. */}
          <ul
            className={cn(
              "flex flex-col gap-4 pl-6 [overflow-anchor:none]",
              i18n.language.startsWith("en") ? LIST_MIN_HEIGHT.en : LIST_MIN_HEIGHT.other,
            )}
          >
            {PERSONAS.map((persona, index) => (
              <PersonaItem
                key={persona.key}
                persona={persona}
                isActive={index === displayIndex}
                userHasInteracted={userHasInteracted}
                onSelect={() => select(index)}
              />
            ))}
          </ul>
        </div>

        <div className="relative aspect-[343/327] w-full shrink-0 overflow-hidden rounded-2xl md:max-w-[480px] md:self-center lg:aspect-[521/589] lg:w-[44%] lg:max-w-[521px] lg:self-start">
          {PERSONAS.map((persona, index) => (
            <Picture
              key={persona.key}
              src={imageUrl(`/images/fleet/fleet-persona-${persona.image}.jpg`)}
              mobileSrc={imageUrl(`/images/fleet/fleet-persona-${persona.image}-mobile.jpg`)}
              alt={t(`persona.items.${persona.key}.image_alt`)}
              aria-hidden={index !== imageIndex}
              loading="lazy"
              className={cn(
                "absolute inset-0 size-full object-cover transition-opacity duration-300",
                index === imageIndex ? "opacity-100" : "opacity-0",
              )}
            />
          ))}
          <div aria-hidden className="absolute inset-0 bg-[rgba(92,92,92,0.2)]" />
        </div>
      </div>
    </section>
  );
}

interface PersonaItemProps {
  persona: Persona;
  isActive: boolean;
  userHasInteracted: boolean;
  onSelect: () => void;
}

function PersonaItem({ persona, isActive, userHasInteracted, onSelect }: PersonaItemProps) {
  const { t } = useTranslation("fleet");
  const title = t(`persona.items.${persona.key}.title`);
  const panelId = `fleet-persona-panel-${persona.key}`;

  return (
    <li className="relative">
      {/* Timer in the list's left gutter, spanning the title and description. */}
      <AutoAdvanceTimer
        isActive={isActive}
        userHasInteracted={userHasInteracted}
        className="absolute top-0 bottom-[52px] -left-6 w-2 rounded-full bg-[#EBEEF3] transition-opacity duration-300"
        fillClassName="bg-bouncie-500 absolute top-0 w-full rounded-full"
        restingFillHeight="8px"
      />
      {/* 15px bottom padding absorbs the 1px divider. */}
      <div className="border-border border-b pb-[15px]">
        <button
          data-sid={`fleet-persona-item:${persona.key}`}
          type="button"
          aria-expanded={isActive}
          aria-controls={panelId}
          onClick={onSelect}
          className={cn(
            "text-text-heading text-md block w-full text-left leading-5 font-bold tracking-[0.18px]",
            isActive && userHasInteracted ? "cursor-default" : "cursor-pointer",
          )}
        >
          {title}
        </button>
        <AutoAdvanceCollapse id={panelId} isActive={isActive} inert={!isActive}>
          <div className="flex flex-col items-start gap-4 pt-4">
            <p className="text-text-body-secondary text-base leading-[1.5]">
              {t(`persona.items.${persona.key}.description`)}
            </p>
            {SHOW_LEARN_MORE && (
              <Button
                asChild
                variant="link"
                size="sm"
                className="text-text-body-tertiary h-auto gap-1 px-0 py-0 text-base leading-5 tracking-[0.16px] underline"
                iconRight={<RiArrowRightSLine aria-hidden className="text-icon-primary size-5" />}
              >
                <a
                  data-sid={`fleet-persona-learn-more:${persona.key}`}
                  href={persona.href}
                  aria-label={t("persona.learn_more_aria", { name: title })}
                >
                  {t("persona.learn_more")}
                </a>
              </Button>
            )}
          </div>
        </AutoAdvanceCollapse>
      </div>
    </li>
  );
}

export default FleetPersonaRouter;
